// SolicitanteUnidadesOrganizacionalesCatalog.jsx
import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './SolicitanteUnidadesOrganizacionalesModal';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import sigmaDelete from '../../../../Pages/img/Borrar.png';

const PAGE_SIZE = 10;
const nombreTabla = 'Solicitante ↔ Unidades Organizacionales';

const endpoint = ENDPOINTS.solicitanteUnidadesOrganizacionales;
const endpointById = ENDPOINTS.solicitanteUnidadesOrganizacionalesById;
const modelo = Models.solicitanteUnidadesOrganizacionales;

// 🔗 Extras para enriquecer Solicitante (Persona) y Unidad Organizacional
const endpointSolicitantes = ENDPOINTS.solicitantes;
const modeloSolicitante = Models.solicitantes;

const endpointEmpleado = ENDPOINTS.empleados;
const modeloEmpleado = Models.empleados;

const endpointPersona = ENDPOINTS.personas;
const modeloPersona = Models.persona;

const endpointUnidadesOrg = ENDPOINTS.unidadesOrg;
const modeloUnidadesOrg = Models.unidadesOrg;

// ⚠️ mapCreate / mapUpdate copiados tal cual del config
const maps = {
  mapCreate: (f) => ({
    idSolicitante: Number(f.idSolicitante) || 0,
    idUnidadOrganizacional: Number(f.idUnidadOrganizacional) || 0,
    activo: !!f.activo,
  }),
  mapUpdate: (id, f) => ({
    idSolicitanteUnidadesOrganizacionales: id,
    idSolicitante: Number(f.idSolicitante) || 0,
    idUnidadOrganizacional: Number(f.idUnidadOrganizacional) || 0,
    activo: !!f.activo,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // relación + solicitantes + empleados + personas + unidades org
      const [
        resRel,
        resSolicitantes,
        resEmpleados,
        resPersonas,
        resUnidadesOrg,
      ] = await Promise.all([
        axios.get(endpoint),
        axios.get(endpointSolicitantes),
        axios.get(endpointEmpleado),
        axios.get(endpointPersona),
        axios.get(endpointUnidadesOrg),
      ]);

      let relNorm = (resRel.data || []).map((x) => modelo(x));
      const solicitantesNorm = (resSolicitantes.data || []).map((x) =>
        modeloSolicitante(x)
      );
      const empleadosNorm = (resEmpleados.data || []).map((x) =>
        modeloEmpleado(x)
      );
      const personasNorm = (resPersonas.data || []).map((x) =>
        modeloPersona(x)
      );
      const unidadesNorm = (resUnidadesOrg.data || []).map((x) =>
        modeloUnidadesOrg(x)
      );

      // Enriquecer cada relación con:
      // - nombreSolicitante (Persona)
      // - nombreUnidadOrg (cat unidades)
      relNorm = relNorm.map((rel) => {
        // --- Solicitante ---
        const sol = solicitantesNorm.find(
          (s) => s.idSolicitante === rel.idSolicitante
        );

        const emp = sol
          ? empleadosNorm.find(
            (e) => e.idEmpleado === sol.idEmpleado
          )
          : null;

        const per = emp
          ? personasNorm.find(
            (p) => p.idPersona === emp.idPersona
          )
          : null;

        const fullName = per
          ? `${per.nombre || ''} ${per.apellidoPaterno || ''} ${per.apellidoMaterno || ''
            }`.trim()
          : '';

        const nombreSolicitante =
          fullName || `Solicitante #${rel.idSolicitante ?? ''}`;

        // --- Unidad Organizacional ---
        const uo = unidadesNorm.find(
          (u) =>
            u.idUnidadOrganizacional === rel.idUnidadOrganizacional
        );

        const nombreUo =
          (uo &&
            (
              uo.nombreUnidadOrganizacional ||
              uo.nombreUnidadOrg ||
              uo.nombre ||
              uo.descripcion ||
              ''
            ).toString()) ||
          '';

        const claveUnidadOrg = uo?.clave || '';
        const nombreUnidadOrg = nombreUo || `Unidad #${rel.idUnidadOrganizacional ?? ''}`;

        return {
          ...rel,
          nombreSolicitante,
          nombreUnidadOrg,
          claveUnidadOrg,
        };
      });

      setRows(relNorm);
    } catch (e) {
      console.error(e);
      alert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const idSol = String(r.idSolicitante ?? '').toLowerCase();
      const idUo = String(r.idUnidadOrganizacional ?? '').toLowerCase();
      const nombreSol = (r.nombreSolicitante || '').toLowerCase();
      const nombreUo = (r.nombreUnidadOrg || '').toLowerCase();
      const claveUo = (r.claveUnidadOrg || '').toLowerCase();
      const activo = r.activo ? 'si' : 'no';

      return (
        idSol.includes(q) ||
        idUo.includes(q) ||
        nombreSol.includes(q) ||
        nombreUo.includes(q) ||
        claveUo.includes(q) ||
        activo.includes(q)
      );
    });
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(
      (x) => x.idSolicitanteUnidadesOrganizacionales === id
    );
    if (!r) {
      alert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(
          endpoint,
          maps.mapCreate(payload)
        );
        const newId =
          res?.data?.idSolicitanteUnidadesOrganizacionales ??
          (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(
          endpoint,
          maps.mapUpdate(selectedId, payload)
        );
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''
        }`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from(
      { length: end - start + 1 },
      (_, i) => start + i
    );
  })();

  const startIndex =
    filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por solicitante, unidad org. o activo…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
          <button
            className="btn-action"
            onClick={handleNew}
          >
            ＋ Nuevo
          </button>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Solicitante</th>
                  <th>Clave Unidad</th>
                  <th>Unidad Organizacional</th>
                  <th style={{ textAlign: 'center' }}>Editar</th>
                  <th style={{ textAlign: 'center' }}>Borrar</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr
                    key={r.idSolicitanteUnidadesOrganizacionales}
                  >
                    <td>{normalizeValue(r.nombreSolicitante)}</td>
                    <td>{normalizeValue(r.claveUnidadOrg)}</td>
                    <td>{normalizeValue(r.nombreUnidadOrg)}</td>

                    {/* EDITAR */}
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--edit"
                        onClick={() =>
                          handleEdit(
                            r.idSolicitanteUnidadesOrganizacionales
                          )
                        }
                        aria-label="Editar"
                        title="Editar"
                      >
                        <img
                          src={sigmaEdit}
                          alt=""
                          className="rk-ico"
                        />
                      </button>
                    </td>

                    {/* BORRAR */}
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--del"
                        onClick={() => {
                          setEditingRowId(null);
                          const nameSol =
                            (r.nombreSolicitante &&
                              r.nombreSolicitante.trim()) ||
                            `Solicitante ${r.idSolicitante}`;
                          const nameUo =
                            (r.nombreUnidadOrg &&
                              r.nombreUnidadOrg.trim()) ||
                            `Unidad ${r.idUnidadOrganizacional}`;
                          askDelete(
                            r.idSolicitanteUnidadesOrganizacionales,
                            `${nameSol} / ${nameUo}`
                          );
                        }}
                        aria-label="Borrar"
                        title="Borrar"
                      >
                        <img
                          src={sigmaDelete}
                          alt=""
                          className="rk-ico"
                        />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                  <tr>
                    <td colSpan={4}>Sin resultados.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0 ? (
              <span>
                Mostrando {startIndex}–{endIndex} de {filtered.length}
              </span>
            ) : (
              <span>0 resultados</span>
            )}
          </div>
          <div className="roles-pagination-right">
            <PageBtn
              label="‹"
              onClick={() =>
                setPage((p) => Math.max(1, p - 1))
              }
              disabled={page === 1}
            />
            {pageNumbers.map((n) => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() =>
                setPage((p) => Math.min(totalPages, p + 1))
              }
              disabled={page === totalPages}
            />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}
